import { isAuthError } from '../hooks/useAsync.js';
import type { DataStore, QueryDescriptor } from './dataStore.js';
import { PREFETCH_STAGES } from './manifest.js';

/**
 * The background fill.
 *
 * It runs once on sign-in and again on demand from Settings, walking `PREFETCH_STAGES` and
 * fetching whatever the store does not already hold fresh. Four rules keep it a *background*
 * job rather than a second application competing with the first:
 *
 * 1. **It yields to the foreground.** Before every request it waits while the store reports a
 *    page-driven fetch in flight. A prefetch that raced the page someone had just opened would
 *    make the app feel slower while claiming to make it faster.
 * 2. **Concurrency is capped at three.** Enough to cover latency, few enough that the browser's
 *    per-host connection budget is never spent entirely on requests nobody asked for.
 * 3. **A failure is recorded and skipped, never retried in a loop and never surfaced as an
 *    error state.** Nothing here is on screen; a page that needs a missing entry will fetch it
 *    and show its own error banner if it fails again.
 * 4. **An authentication failure stops the whole run.** A revoked session turns every remaining
 *    request into another 401, and the app is about to send the reader to sign in anyway.
 *
 * Progress is observable because it has to be explainable: Settings shows what was fetched, what
 * was already fresh, and what failed. A silent background process that a reader cannot inspect is
 * indistinguishable from one that is not running.
 */

const CONCURRENCY = 3;

/** Yield ceiling, so a permanently busy foreground cannot stall the run forever. */
const MAX_YIELD_MS = 10_000;
const YIELD_STEP_MS = 250;

export type PrefetchPhase = 'idle' | 'running' | 'done' | 'stopped';

export interface PrefetchProgress {
  phase: PrefetchPhase;
  /** Stage currently being walked, for the readout. */
  stage: string | null;
  /** Requests issued and completed, successfully or not. */
  completed: number;
  /** Requests the plan asked for, growing as derived stages resolve. */
  total: number;
  /** Entries already fresh, so no request was made. */
  skipped: number;
  failed: number;
  startedAt: number | null;
  finishedAt: number | null;
}

const IDLE: PrefetchProgress = {
  phase: 'idle',
  stage: null,
  completed: 0,
  total: 0,
  skipped: 0,
  failed: 0,
  startedAt: null,
  finishedAt: null,
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class Prefetcher {
  private progress: PrefetchProgress = IDLE;
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private cancelled = false;

  constructor(private readonly store: DataStore) {}

  getProgress = (): PrefetchProgress => this.progress;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * Start a run, or join the one already going.
   *
   * Joining rather than queueing is the right behaviour for both callers: a second sign-in event
   * should not fill the store twice, and a reader who presses Refresh while a run is in flight
   * means "make sure it is filled", which the running pass is already doing.
   */
  start(): Promise<void> {
    if (this.running) {
      // Un-cancel. React remounts an effect in development (StrictMode), so the first pass's
      // cleanup calls `stop()` between the two `start()` calls — and a run joined in that state
      // would end early and never resume, leaving the store half full in dev only.
      this.cancelled = false;
      return this.running;
    }

    this.cancelled = false;
    this.progress = {
      ...IDLE,
      phase: 'running',
      startedAt: Date.now(),
    };
    this.emit();

    this.running = this.walk()
      .then(() => {
        this.set({
          phase: this.cancelled ? 'stopped' : 'done',
          stage: null,
          finishedAt: Date.now(),
        });
      })
      .finally(() => {
        this.running = null;
      });

    return this.running;
  }

  /** Stop after the requests already in flight. Used on sign-out. */
  stop(): void {
    this.cancelled = true;
  }

  private async walk(): Promise<void> {
    for (const stage of PREFETCH_STAGES) {
      if (this.cancelled) return;

      // Built here, not up front: a derived stage reads what the previous stage stored.
      const descriptors = stage.build(this.store);
      this.set({ stage: stage.name, total: this.progress.total + descriptors.length });

      const pending = descriptors.filter((d) => {
        if (this.store.freshness(d.key, Date.now(), d.staleWhen) === 'fresh') {
          this.set({ skipped: this.progress.skipped + 1 });
          return false;
        }
        return true;
      });

      await this.drain(pending);
    }
  }

  /** Run a stage's requests `CONCURRENCY` at a time, yielding to the foreground between each. */
  private async drain(descriptors: Array<QueryDescriptor<unknown>>): Promise<void> {
    let cursor = 0;

    const worker = async (): Promise<void> => {
      while (cursor < descriptors.length && !this.cancelled) {
        const descriptor = descriptors[cursor];
        cursor += 1;
        if (!descriptor) return;

        await this.yieldToForeground();
        if (this.cancelled) return;

        try {
          await this.store.run(descriptor, { background: true });
        } catch (err) {
          // Rule 4: a revoked or expired session makes every remaining request pointless.
          if (isAuthError(err instanceof Error ? err : null)) {
            this.cancelled = true;
            return;
          }
          // A feature switched off by its operator is not a failure (spec 007, FR-AU38): with
          // AUDIT_ENABLED=false the Audit list answers 503 AUDIT_DISABLED, and counting that as
          // "failed" would change the Settings readout of a page that is supposed to be untouched.
          if ((err as { code?: unknown } | null)?.code === 'AUDIT_DISABLED') {
            this.set({ skipped: this.progress.skipped + 1 });
            continue;
          }
          this.set({ failed: this.progress.failed + 1 });
        } finally {
          this.set({ completed: this.progress.completed + 1 });
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, descriptors.length) }, () => worker()),
    );
  }

  private async yieldToForeground(): Promise<void> {
    let waited = 0;
    while (this.store.foregroundBusy && waited < MAX_YIELD_MS && !this.cancelled) {
      await sleep(YIELD_STEP_MS);
      waited += YIELD_STEP_MS;
    }
  }

  private set(patch: Partial<PrefetchProgress>): void {
    this.progress = { ...this.progress, ...patch };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
